import type {
  EventLayout,
  TimeGridEvent,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

// Orders events deterministically: start asc, longer first, id asc.
const getEventStart = (event: TimeGridEvent): Date => event.eventStart ?? event.start
const getEventEnd = (event: TimeGridEvent): Date => event.eventEnd ?? event.end

const compareEvents = <TEvent extends TimeGridEvent>(left: TEvent, right: TEvent): number => {
  const leftStart = getEventStart(left)
  const rightStart = getEventStart(right)

  if (leftStart < rightStart) return -1
  if (leftStart > rightStart) return 1

  const leftEnd = getEventEnd(left)
  const rightEnd = getEventEnd(right)

  const leftDuration = leftEnd.getTime() - leftStart.getTime()
  const rightDuration = rightEnd.getTime() - rightStart.getTime()

  if (leftDuration > rightDuration) return -1
  if (leftDuration < rightDuration) return 1

  return left.id.localeCompare(right.id)
}

// Returns events assigned to vertical stacks within a row.
export const stackEvents = <TEvent extends TimeGridEvent>({
  layouts,
  overlaps,
}: {
  layouts: EventLayout<TEvent>[]
  overlaps: (left: EventLayout<TEvent>, right: EventLayout<TEvent>) => boolean
}): { stacked: EventLayout<TEvent>[]; rowStackCount: number } => {
  const sortedLayouts = [...layouts].sort((left, right) => compareEvents(left.event, right.event))
  const stacks: EventLayout<TEvent>[][] = []
  const stackedLayouts: EventLayout<TEvent>[] = []

  sortedLayouts.forEach((layout) => {
    const stackIndex = stacks.findIndex((stack) => stack.every((item) => !overlaps(layout, item)))
    const resolvedIndex = stackIndex === -1 ? stacks.length : stackIndex

    if (!stacks[resolvedIndex]) stacks[resolvedIndex] = []
    stacks[resolvedIndex].push(layout)

    stackedLayouts.push({ ...layout, stackIndex: resolvedIndex })
  })

  return { stacked: stackedLayouts, rowStackCount: Math.max(stacks.length, 1) }
}
